import React, {useEffect, useRef, useState} from "react";
import "./_flowchart-custom-edge.scss";
import {BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath} from "@xyflow/react";
import {useAppSelector} from "../../../../store/hooks";
import {familyMembersSelector} from "../../../slices/EstateFlowChartSlice";
import {NO_OP} from "../../../../constants/common";
import {getMemberNameById} from "../../../utils/DefUtils";
import {Icon} from "../../../../components";
import {AdditionalDistributionLayer} from "../../../model/EstateFlowchartTypes";

export type FlowchartCustomEdgeProps = {
    memberId: string,
    width: number,
    shouldShowEstateTaxDuePill?: boolean,
    additionalDistribution?: AdditionalDistributionLayer
}

type FlowchartEdgeProps = EdgeProps & {
    data: FlowchartCustomEdgeProps
}

const FlowchartCustomEdge = ({
                                 sourceX,
                                 sourceY,
                                 targetX,
                                 targetY,
                                 sourcePosition,
                                 targetPosition,
                                 markerEnd,
                                 data,
                             }: FlowchartEdgeProps) => {
    const [memberName, setMemberName] = useState<string | null>("");
    const familyMembers = useAppSelector(familyMembersSelector);

    const [additionalDistributionPillText, setAdditionalDistributionPillText] = useState<string | undefined>(undefined);

    const afterDeathPillRef = useRef<HTMLDivElement | null>(null);
    const estateTaxDuePillRef = useRef<HTMLDivElement | null>(null);

    const [afterDeathPillWidth, setAfterDeathPillWidth] = useState<number>(0);
    const [estateTaxDuePillWidth, setEstateTaxDuePillWidth] = useState<number>(0);

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
    });

    const secondDeathText = 'After Second Death';
    useEffect(() => {
        if (familyMembers) {
            if(data.memberId==="partnerMember" && !getMemberNameById(familyMembers, data.memberId )) {
                setMemberName(secondDeathText);
            } else {
                setMemberName(getMemberNameById(familyMembers, data.memberId));
            }
        }

        setAdditionalDistributionPillText(
            getAdditionalDistributionPillText(familyMembers, data.additionalDistribution)
        );
    }, [familyMembers, data.additionalDistribution, data.memberId]);

    useEffect(() => {
        if (afterDeathPillRef.current) {
            setAfterDeathPillWidth(afterDeathPillRef.current.offsetWidth);
        }
        if (data.shouldShowEstateTaxDuePill) {
            if (estateTaxDuePillRef.current) {
                setEstateTaxDuePillWidth(estateTaxDuePillRef.current.offsetWidth);
            }
        }
    }, [memberName, additionalDistributionPillText]);

    function getPillText() {
        if (data.additionalDistribution && additionalDistributionPillText) {
            return additionalDistributionPillText;
        }
        return memberName === secondDeathText ? secondDeathText : `After Death of ${memberName}`;
    }

    const getAdditionalDistributionPillText = (
        familyMembers: any,
        additionalDistribution?: AdditionalDistributionLayer
    ): string | undefined => {
        if (!additionalDistribution) return undefined;

        const trigger = additionalDistribution;
        switch (trigger.triggerEventType) {
            case "DEATH": {
                const resolvedName = getMemberNameById(familyMembers, trigger.triggerMemberId);
                return resolvedName ? `After Death of ${resolvedName}` : undefined;
            }
            case "AGE_ATTAINMENT": {
                const resolvedName = getMemberNameById(familyMembers, trigger.triggerMemberId);
                const age = trigger.triggerAge;
                return resolvedName && age !== null && age !== undefined
                    ? `After ${resolvedName} Turns ${age}`
                    : undefined;
            }
            case "OTHER": {
                const txt = (trigger.triggerEventText ?? "").trim();
                return txt ? `After ${txt}` : undefined;
            }
            default:
                return undefined;
        }
    }

    return (
        <>
            <BaseEdge path={edgePath} markerEnd={markerEnd} style={{strokeWidth: 2, stroke: '#CECECE'}}
                      onClick={NO_OP}/>
            <EdgeLabelRenderer>
                <div
                    className="button-edge__label nodrag nopan"
                    style={{
                        transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                        width: data.width,
                    }}
                >
                    <div ref={afterDeathPillRef}
                         style={{
                             transformOrigin: "top right",
                             transform: data.shouldShowEstateTaxDuePill
                                 ? `translate(-50%, -50%) translateX(-${afterDeathPillWidth / 2 + 16}px)`
                                 : '',
                         }} className={"flowchart-pill-container"}>
                        <span
                            className={"pill-text"}>{memberName ? getPillText() : `After First Death`}</span>
                    </div>
                    {data.shouldShowEstateTaxDuePill && <div ref={estateTaxDuePillRef}
                         style={{
                             transformOrigin: "top left",
                             transform: `translate(-50%, -50%) translateX(${estateTaxDuePillWidth / 2 + 16}px)`,
                         }} className={"flowchart-pill-container estate-tax-due-pill"}>
                        <Icon name="currency_dollar" className="currency-dollar-icon"/>
                        <span className={"pill-text"}>{"Estate Tax Due"}</span>
                    </div>}
                    <div className={"flowchart-level-separator"} style={{width: data.width}}/>
                </div>
            </EdgeLabelRenderer>
        </>
    )
}

export default FlowchartCustomEdge;

